import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import FileUploadComponent from './FileUploadComponent';
import { useQuotes } from '../hooks/useQuotes';
import { useAuth } from '../lib/AuthContext';
import { moderateContent } from '../lib/moderation';

interface ParsedQuote {
  text: string;
  author: string;
  context?: string;
  year?: number;
  lineNumber?: number;
  errors?: string[];
}

interface UploadResult {
  success: number;
  failed: number;
  errors: string[];
  duplicates: number;
}

export default function BatchUploadPage() {
  const navigate = useNavigate();
  const { addQuote } = useQuotes();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  // Rate limiting check
  const checkRateLimit = async (): Promise<boolean> => {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      
      // This would ideally be checked against a server-side rate limiting store
      // For now, we'll implement a client-side check with localStorage
      const rateLimitKey = `upload_rate_limit_${user?.id}`;
      const storedData = localStorage.getItem(rateLimitKey);
      
      if (storedData) {
        const { count, timestamp } = JSON.parse(storedData);
        const hourAgo = Date.now() - 60 * 60 * 1000;
        
        if (timestamp > hourAgo && count >= 100) {
          setRateLimitError('Rate limit exceeded. You can upload a maximum of 100 quotes per hour.');
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Rate limit check error:', error);
      return true; // Fail open
    }
  };

  const updateRateLimit = (quotesCount: number) => {
    const rateLimitKey = `upload_rate_limit_${user?.id}`;
    const storedData = localStorage.getItem(rateLimitKey);
    
    let count = quotesCount;
    const timestamp = Date.now();
    
    if (storedData) {
      const { count: existingCount, timestamp: existingTimestamp } = JSON.parse(storedData);
      const hourAgo = Date.now() - 60 * 60 * 1000;
      
      if (existingTimestamp > hourAgo) {
        count += existingCount;
      }
    }
    
    localStorage.setItem(rateLimitKey, JSON.stringify({ count, timestamp }));
  };

  const checkForDuplicates = async (quotes: ParsedQuote[]): Promise<ParsedQuote[]> => {
    // This would ideally be done server-side with a database query
    // For now, we'll implement basic duplicate detection
    const uniqueQuotes = quotes.filter((quote, index, array) => {
      return array.findIndex(q => 
        q.text.toLowerCase().trim() === quote.text.toLowerCase().trim() && 
        q.author.toLowerCase().trim() === quote.author.toLowerCase().trim()
      ) === index;
    });
    
    return uniqueQuotes;
  };

  const processQuotes = async (quotes: ParsedQuote[]) => {
    setIsProcessing(true);
    setUploadResult(null);
    setRateLimitError(null);

    try {
      // Check rate limit
      const canUpload = await checkRateLimit();
      if (!canUpload) {
        setIsProcessing(false);
        return;
      }

      // Check for duplicates
      const uniqueQuotes = await checkForDuplicates(quotes);
      const duplicatesCount = quotes.length - uniqueQuotes.length;

      const result: UploadResult = {
        success: 0,
        failed: 0,
        errors: [],
        duplicates: duplicatesCount
      };

      // Process quotes in batches
      const batchSize = 10;
      for (let i = 0; i < uniqueQuotes.length; i += batchSize) {
        const batch = uniqueQuotes.slice(i, i + batchSize);
        
        for (const quote of batch) {
          try {
            // Content moderation
            const moderationResult = await moderateContent(quote.text);
            if (!moderationResult.isAcceptable) {
              result.failed++;
              result.errors.push(`Quote at line ${quote.lineNumber}: Content moderation failed - ${moderationResult.violations?.join(', ')}`);
              continue;
            }

            // Add quote
            const addResult = await addQuote({
              text: quote.text,
              author: quote.author,
              age: null,
              location: null,
              context: quote.context || null,
              year: quote.year || null,
              isFavorite: false,
              quotewall: false
            });

            if (addResult.success) {
              result.success++;
            } else {
              result.failed++;
              result.errors.push(`Quote at line ${quote.lineNumber}: ${addResult.error}`);
            }
          } catch (error) {
            result.failed++;
            result.errors.push(`Quote at line ${quote.lineNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }

        // Small delay between batches to prevent overwhelming the server
        if (i + batchSize < uniqueQuotes.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Update rate limit
      updateRateLimit(result.success);

      // Log upload activity
      console.log(`Batch upload completed for user ${user?.id}:`, {
        totalProcessed: quotes.length,
        successful: result.success,
        failed: result.failed,
        duplicates: result.duplicates,
        timestamp: new Date().toISOString()
      });

      setUploadResult(result);
    } catch (error) {
      console.error('Batch upload error:', error);
      setUploadResult({
        success: 0,
        failed: quotes.length,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred'],
        duplicates: 0
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/quotes')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Quotes</span>
          </button>
          
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 rounded-full text-brand-600 font-medium mb-6"
            >
              <Upload className="h-4 w-4" />
              <span>Batch Upload</span>
            </motion.div>
            
            <h1 className="text-3xl font-display font-bold text-gray-900 mb-4">
              Upload Multiple Quotes from Files
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Import quotes in bulk from text, CSV, or Excel files. 
              Perfect for migrating existing quote collections.
            </p>
          </div>
        </div>

        {/* Rate Limit Notice */}
        <div className="mb-6 card p-4 bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-800">
              Rate limit: 100 quotes per hour per user for security and performance
            </span>
          </div>
        </div>

        {/* Rate Limit Error */}
        {rateLimitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 card p-4 bg-red-50 border border-red-200"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-sm text-red-800">{rateLimitError}</span>
            </div>
          </motion.div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 card p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <h2 className="text-xl font-semibold text-gray-900">Upload Complete</h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{uploadResult.success}</p>
                <p className="text-sm text-green-700">Successfully imported</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{uploadResult.failed}</p>
                <p className="text-sm text-red-700">Failed to import</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{uploadResult.duplicates}</p>
                <p className="text-sm text-yellow-700">Duplicates skipped</p>
              </div>
            </div>

            {uploadResult.errors.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-medium text-red-800 mb-2">Errors encountered:</h3>
                <div className="max-h-40 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                    {uploadResult.errors.length > 10 && (
                      <li className="text-red-600 font-medium">
                        ... and {uploadResult.errors.length - 10} more errors
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-center">
              <button
                onClick={() => navigate('/quotes')}
                className="btn-primary"
              >
                View Your Quotes
              </button>
            </div>
          </motion.div>
        )}

        {/* File Upload Component */}
        {!isProcessing && !uploadResult && (
          <FileUploadComponent
            onQuotesExtracted={processQuotes}
            maxFiles={10}
            maxFileSize={10 * 1024 * 1024}
          />
        )}

        {/* Processing State */}
        {isProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card p-8 text-center"
          >
            <div className="animate-spin h-12 w-12 border-4 border-brand-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Processing Your Quotes
            </h3>
            <p className="text-gray-600">
              Please wait while we validate and import your quotes. This may take a few moments.
            </p>
          </motion.div>
        )}

        {/* Security Notice */}
        <div className="mt-8 card p-6 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Security & Privacy</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• All uploaded files are processed locally and not stored on our servers</p>
            <p>• Content is automatically moderated for appropriate language</p>
            <p>• Duplicate quotes are automatically detected and skipped</p>
            <p>• Upload activities are logged for security and audit purposes</p>
            <p>• Rate limiting is enforced to prevent abuse (100 quotes per hour)</p>
          </div>
        </div>
      </div>
    </div>
  );
}